import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

import { StepperService } from '../stepper.service';
import { ClientService } from 'src/app/services/client.service';
import { Payer } from 'src/app/domain/Payer';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TranslateModule } from '@ngx-translate/core';
import { Shipment } from 'src/app/domain/Shipment';

type PayerType = 'franco' | 'ufranko';

@Component({
  selector: 'app-payer',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, ButtonModule, DropdownModule, SelectButtonModule, TranslateModule],
  templateUrl: './payer.component.html',
  styleUrls: ['./payer.component.scss'],
})
export class PayerComponent implements OnInit, OnDestroy {
  shipment: Shipment;
  payers: Payer[] = [];

  payerTypeOptions: { label: string; value: PayerType }[] = [
    { label: 'FRANCO', value: 'franco' },
    { label: 'UFRANKO', value: 'ufranko' }
  ];

  payerFormGroup = new FormGroup({
    payerTypeControl: new FormControl<PayerType>('franco', { nonNullable: true, validators: [Validators.required], }),
    paymentChoiceControl: new FormControl<Payer | null>({ value: null, disabled: true, }),
  });

  private subscriptions = new Subscription();

  constructor(public stepperService: StepperService, private clientService: ClientService) {
    this.shipment = this.stepperService.newShipment;
    this.payers = this.clientService.clientPayers ?? [];
  }

  ngOnInit(): void {
    if (this.shipment.clientPayerId) {
      let payer = this.payers.find(x => x.id === this.shipment.clientPayerId) ?? null;
      this.payerFormGroup.controls.paymentChoiceControl.setValue(payer, { emitEvent: false });
      this.payerFormGroup.controls.payerTypeControl.setValue('ufranko', { emitEvent: false });
    }
    this.subscriptions.add(this.payerFormGroup.controls.payerTypeControl.valueChanges.subscribe(() => { this.onPayerTypeChange(); }));
    this.subscriptions.add(this.payerFormGroup.controls.paymentChoiceControl.valueChanges.subscribe((payer) => { this.onPaymentChange(payer); }));
    this.onPayerTypeChange();
  }

  ngOnDestroy(): void {
    this.shipment.ufranko = this.payerFormGroup.controls.payerTypeControl.getRawValue() == "franco" ? false : true;
    this.subscriptions.unsubscribe();
  }

  onPayerTypeChange(): void {    
    let payerType = this.payerFormGroup.controls.payerTypeControl.value;
    let paymentChoiceControl = this.payerFormGroup.controls.paymentChoiceControl;

    if (payerType === 'ufranko') {
      paymentChoiceControl.enable({ emitEvent: false });
      paymentChoiceControl.setValidators([Validators.required]);

      if (this.payers.length === 1) {
        let payer = this.payers[0];

        paymentChoiceControl.setValue(payer, { emitEvent: false });
        this.onPaymentChange(payer);
      }
    } else {
      paymentChoiceControl.disable({ emitEvent: false });
      paymentChoiceControl.clearValidators();
      paymentChoiceControl.setValue(null, { emitEvent: false });

      this.onPaymentChange(null);
    }

    paymentChoiceControl.updateValueAndValidity({ emitEvent: false });
  }

  onPaymentChange(payer: Payer | null): void {
    this.shipment.clientPayerId = payer?.id ?? null;
    this.shipment.reference3 = payer?.accountNumber ?? null;
  }

  get payerFormInvalid(): boolean {
    return this.payerFormGroup.invalid;
  }
}